import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Users,
    Shield,
    Edit,
    Trash2,
    Plus,
    Search,
    Filter,
    MoreVertical,
    UserCheck,
    UserX,
    Crown,
    Users2,
    UserPlus
} from "lucide-react";
import BackButton from "../../components/BackButton";

const ManageRoles = () => {
    const [roles, setRoles] = useState([
        {
            id: 1,
            name: "Administrator",
            description: "Full system access and management",
            users: 3,
            permissions: 15,
            color: "from-purple-500 to-purple-600",
            icon: <Crown className="w-5 h-5" />
        },
        {
            id: 2,
            name: "Project Manager",
            description: "Project oversight and team leadership",
            users: 8,
            permissions: 10,
            color: "from-blue-500 to-blue-600",
            icon: <Users2 className="w-5 h-5" />
        },
        {
            id: 3,
            name: "Team Member",
            description: "Basic project access and collaboration",
            users: 24,
            permissions: 5,
            color: "from-green-500 to-green-600",
            icon: <Users className="w-5 h-5" />
        }
    ]);

    const [newRole, setNewRole] = useState("");
    const [newDescription, setNewDescription] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [editingRole, setEditingRole] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const addRole = () => {
        if (newRole.trim() && newDescription.trim() && !roles.find(role => role.name === newRole)) {
            const newRoleObj = {
                id: roles.length + 1,
                name: newRole,
                description: newDescription,
                users: 0,
                permissions: 0,
                color: "from-gray-500 to-gray-600",
                icon: <Users className="w-5 h-5" />
            };
            setRoles([...roles, newRoleObj]);
            setNewRole("");
            setNewDescription("");
            setShowAddForm(false);
        }
    };

    const updateRole = (id) => {
        if (newRole.trim() && newDescription.trim()) {
            setRoles(roles.map(role =>
                role.id === id
                    ? { ...role, name: newRole, description: newDescription }
                    : role
            ));
            setNewRole("");
            setNewDescription("");
            setEditingRole(null);
        }
    };

    const deleteRole = (id) => {
        setRoles(roles.filter(role => role.id !== id));
        setShowDeleteConfirm(null);
    };

    const filteredRoles = roles.filter(role =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <BackButton to="/admin/dashboard" />
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Manage Roles</h1>
                                <p className="text-gray-600 mt-1">Define and manage user roles and permissions</p>
                            </div>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowAddForm(true)}
                            className="btn-primary flex items-center"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Add New Role
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-6 py-8">
                {/* Search and Filter */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search roles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                        <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                            <Filter className="w-5 h-5 mr-2" />
                            Filter
                        </button>
                    </div>
                </div>

                {/* Add/Edit Role Form */}
                {(showAddForm || editingRole) && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card mb-8"
                    >
                        <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            {editingRole ? "Edit Role" : "Add New Role"}
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Role Name
                                </label>
                                <input
                                    type="text"
                                    value={newRole}
                                    placeholder="Enter role name"
                                    onChange={(e) => setNewRole(e.target.value)}
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <input
                                    type="text"
                                    value={newDescription}
                                    placeholder="Enter role description"
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    className="input-field"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingRole(null);
                                    setNewRole("");
                                    setNewDescription("");
                                }}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => editingRole ? updateRole(editingRole.id) : addRole()}
                                className="btn-primary"
                            >
                                {editingRole ? "Update Role" : "Add Role"}
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Roles Grid */}
                <div className="grid gap-6">
                    {filteredRoles.map((role, index) => (
                        <motion.div
                            key={role.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="card hover:shadow-medium group"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-4 flex-1">
                                    <div className={`p-3 rounded-xl bg-gradient-to-br ${role.color} bg-opacity-10`}>
                                        <div className={`text-transparent bg-gradient-to-br ${role.color} bg-clip-text`}>
                                            {role.icon}
                                        </div>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="text-xl font-semibold text-gray-900">{role.name}</h3>
                                            <span className="badge-success text-xs px-2 py-1">Active</span>
                                        </div>
                                        <p className="text-gray-600 mb-4">{role.description}</p>

                                        <div className="flex items-center space-x-6">
                                            <div className="flex items-center space-x-2">
                                                <UserCheck className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm text-gray-600">
                          <span className="font-semibold text-gray-900">{role.users}</span> users
                        </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Shield className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm text-gray-600">
                          <span className="font-semibold text-gray-900">{role.permissions}</span> permissions
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => {
                                            setEditingRole(role);
                                            setNewRole(role.name);
                                            setNewDescription(role.description);
                                        }}
                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Edit role"
                                    >
                                        <Edit className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteConfirm(role.id)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete role"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                    <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                        <MoreVertical className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {filteredRoles.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-16"
                    >
                        <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                            <Users className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                            {searchTerm
                                ? "No roles match your search criteria. Try a different search term."
                                : "Get started by creating your first role to define user permissions."
                            }
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="btn-primary"
                            >
                                <UserPlus className="w-5 h-5 mr-2" />
                                Create Your First Role
                            </button>
                        )}
                    </motion.div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl shadow-strong max-w-md w-full p-6"
                        >
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <UserX className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                                Delete Role
                            </h3>
                            <p className="text-gray-600 text-center mb-6">
                                Are you sure you want to delete this role? This action cannot be undone.
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => deleteRole(showDeleteConfirm)}
                                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Delete Role
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Stats Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-12"
                >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="card text-center">
                            <div className="text-3xl font-bold text-gray-900 mb-2">
                                {roles.length}
                            </div>
                            <div className="text-sm text-gray-600">Total Roles</div>
                        </div>
                        <div className="card text-center">
                            <div className="text-3xl font-bold text-gray-900 mb-2">
                                {roles.reduce((sum, role) => sum + role.users, 0)}
                            </div>
                            <div className="text-sm text-gray-600">Total Users</div>
                        </div>
                        <div className="card text-center">
                            <div className="text-3xl font-bold text-gray-900 mb-2">
                                {roles.reduce((sum, role) => sum + role.permissions, 0)}
                            </div>
                            <div className="text-sm text-gray-600">Total Permissions</div>
                        </div>
                        <div className="card text-center">
                            <div className="text-3xl font-bold text-gray-900 mb-2">
                                {Math.round(roles.reduce((sum, role) => sum + role.permissions, 0) / roles.length) || 0}
                            </div>
                            <div className="text-sm text-gray-600">Avg. Permissions per Role</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ManageRoles;